import React from "react";
import Timeline from "react-calendar-timeline";
import moment from "moment-timezone";
import values from "lodash/values";
import debounce from "lodash/debounce";
import _ from "lodash";
import { getLuminance } from "material-ui/utils/colorManipulator";

import LinkLike from "../link-like.jsx";
import { getColorForTag } from "../../../util";

const INSTRUCTIONS_STYLE = {
  textAlign: "center",
  margin: "20px 10px 10px",
  fontStyle: "italic"
};
const KEY_STYLE = {
  border: "1px #ccc solid",
  padding: 5,
  margin: "10px 0",
  float: "right",
  maxWidth: 250,
  wordBreak: "break-all"
};
const MOBILE_KEY_STYLE = {
  margin: "10px auto 0",
  float: "none"
};
const KEY_TITLE_STYLE = {
  fontWeight: "bold",
  textAlign: "center"
};
const KEY_SWATCH_STYLE = {
  display: "inline-block",
  width: 12,
  height: 12,
  marginRight: 5,
  verticalAlign: "-2px"
};

const UNKNOWN_GROUP = { id: "UNKNOWN_GROUP", title: "Unknown Venue" };
const UNKNOWN_TAG = { id: "UNKNOWN_TAG", color: "#ccc", hideInKey: true };

class EventTimeline extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      groups: [],
      primaryTags: [],
      earliestTime: null,
      latestTime: null,
      startTime: null,
      endTime: null
    };

    this.onItemSelect = this.onItemSelect.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
    this.generateStyles = this.generateStyles.bind(this);
    this.generateKey = this.generateKey.bind(this);
    this.updateUrlState = debounce(this.updateUrlState.bind(this), 50);
    this.zoomToAll = this.zoomToAll.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      isLarge: React.PropTypes.bool
    };
  }

  componentDidMount() {
    // Timeline wants to show all historical events
    if (this.props.fetchPast) {
      this.props.fetchPast();
    }

    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(newProps) {
    let startTime, endTime;
    if (newProps.range) {
      [startTime, endTime] = newProps.range.split(",");
      if (
        startTime === this.state.startTime &&
        endTime === this.state.endTime
      ) {
        return;
      }
    }

    let { events, venues } = newProps,
      hasUnknownVenue = false,
      currentlyShownVenues = {},
      primaryTags = { [UNKNOWN_TAG.id]: UNKNOWN_TAG },
      earliestTime,
      latestTime,
      now = moment(),
      closestToAfterNow;

    let items = _(Object.keys(events))
      .filter(id => {
        return !!events[id].start_time && events[id].is_dated;
      })
      .sortBy(id => events[id].start_time)
      .map(id => {
        let event = events[id],
          start = moment(event.start_time),
          end = moment(event.end_time);

        if (start && (!earliestTime || start < earliestTime)) {
          earliestTime = start;
        }
        if (end && (!latestTime || end > latestTime)) {
          latestTime = end;
        }
        if (!closestToAfterNow && start > now) {
          closestToAfterNow = start;
        }

        if (
          event.tags.length &&
          event.tags.find(tag => tag.visible) &&
          !primaryTags[event.tags.find(tag => tag.visible).id]
        ) {
          const primaryTag = event.tags.find(tag => tag.visible);
          let color = getColorForTag(primaryTag, true, 1, this.context.tenant);
          primaryTags[primaryTag.id] = {
            name: primaryTag.name,
            color: color,
            dark_text: getLuminance(color) > 0.5
          };
        }

        if (!event.venue) {
          hasUnknownVenue = true;
        } else {
          currentlyShownVenues[event.venue.id] = true;
        }

        return {
          id: id,
          title: event.name,
          group: event.venue ? event.venue.id : UNKNOWN_GROUP.id,
          start_time: start,
          end_time: end,
          className: `tag-${
            event.tags.length ? event.tags[0].id : UNKNOWN_TAG.id
          }`
        };
      })
      .value();

    let groups = venues
      .filter(v => currentlyShownVenues[v.id])
      .map(v => {
        return {
          id: v.id,
          title: v.name
        };
      });

    if (hasUnknownVenue) {
      groups = groups.concat([UNKNOWN_GROUP]);
    }

    let earliestBuffered = moment(earliestTime).add(-1, "hours").valueOf(),
      latestBuffered = moment(latestTime).add(1, "hours").valueOf();

    this.setState({
      items,
      groups,
      primaryTags: primaryTags,
      earliestTime: earliestBuffered,
      latestTime: latestBuffered,
      startTime:
        +startTime ||
        (closestToAfterNow
          ? moment(closestToAfterNow).add(-1, "hours").valueOf()
          : earliestBuffered),
      endTime:
        +endTime ||
        (closestToAfterNow
          ? moment(closestToAfterNow).add(12, "hours").valueOf()
          : latestBuffered)
    });
  }

  onItemSelect(id) {
    let { startTime, endTime } = this.state;
    this.context.router.push({
      pathname: `/schedule/${id}`,
      state: { fromSchedule: true }
    });
  }

  onTimeChange(proposedStartTime, proposedEndTime) {
    let { startTime, endTime, earliestTime, latestTime } = this.state,
      updatedStartTime,
      updatedEndTime;

    if (proposedStartTime < earliestTime && proposedEndTime > latestTime) {
      updatedStartTime = earliestTime;
      updatedEndTime = latestTime;
    } else if (proposedStartTime < earliestTime) {
      updatedStartTime = earliestTime;
      updatedEndTime = earliestTime + (proposedEndTime - proposedStartTime);
    } else if (proposedEndTime > latestTime) {
      updatedStartTime = latestTime - (proposedEndTime - proposedStartTime);
      updatedEndTime = latestTime;
    } else {
      updatedStartTime = proposedStartTime;
      updatedEndTime = proposedEndTime;
    }

    if (this.timeline) {
      this.timeline.updateScrollCanvas(updatedStartTime, updatedEndTime);
    }
    this.updateUrlState(updatedStartTime, updatedEndTime);
  }

  updateUrlState(startTime, endTime) {
    this.props.onStateChange({ range: `${startTime},${endTime}` });
  }

  zoomToAll() {
    this.onTimeChange(0, Number.MAX_SAFE_INTEGER);
  }

  generateStyles() {
    let { primaryTags } = this.state,
      { palette } = this.context.tenant;

    return `
      .EventTimeline .rct-label-group,
      .EventTimeline .rct-sidebar-header,
      .EventTimeline .rct-label.rct-label-only {
        background: ${palette.primary1Color} !important;
      }

      ${Object.keys(primaryTags)
        .map(id => {
          return `
          .EventTimeline .tag-${id} {
            background: ${primaryTags[id].color} !important;
            border: 1px solid ${primaryTags[id].color} !important;
            color: ${primaryTags[id].dark_text ? "#000" : "#fff"} !important;
          }
        `;
        })
        .join("\n")}
    `;
  }

  generateKey() {
    let { primaryTags } = this.state,
      { isLarge } = this.context;

    let keyTags = Object.keys(primaryTags)
      .filter(id => !primaryTags[id].hideInKey)
      .map(id => {
        return primaryTags[id];
      });

    // sort in alphabetical order to avoid random display - BUG BASH
    keyTags = keyTags.sort((a, b) => {
      if (a.name > b.name) {
        return 1;
      } else if (a.name < b.name) {
        return -1;
      } else {
        return 0;
      }
    });

    return (
      <div style={Object.assign({}, KEY_STYLE, !isLarge && MOBILE_KEY_STYLE)}>
        <div style={KEY_TITLE_STYLE}>Key</div>
        {keyTags.map(tag => {
          return (
            <div>
              <span
                style={Object.assign({}, KEY_SWATCH_STYLE, {
                  backgroundColor: tag.color
                })}
              />
              {tag.name}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    let { items, groups, primaryTags, startTime, endTime } = this.state;

    // Wait until we have some initial items before rendering anything
    if (!items.length) {
      return null;
    }

    return (
      <div className="EventTimeline">
        <style>{this.generateStyles()}</style>
        <div style={INSTRUCTIONS_STYLE}>
          <div>
            Pinch to zoom, drag or scroll left and right to navigate, and then
            click events to learn more.
          </div>
          <div>
            Manual controls:&nbsp;
            <LinkLike onClick={this.zoomToAll}>[show all]</LinkLike>&nbsp;
            <LinkLike
              onClick={() => this.timeline && this.timeline.changeZoom(0.75)}
            >
              [zoom in]
            </LinkLike>
            &nbsp;
            <LinkLike
              onClick={() => this.timeline && this.timeline.changeZoom(1.25)}
            >
              [zoom out]
            </LinkLike>
          </div>
        </div>
        <Timeline
          ref={r => {
            this.timeline = r;
          }}
          groups={groups}
          items={items}
          canMove={false}
          canChangeGroup={false}
          canResize={false}
          stackItems={true}
          onItemSelect={this.onItemSelect}
          visibleTimeStart={startTime}
          visibleTimeEnd={endTime}
          onTimeChange={this.onTimeChange}
        />
        {this.generateKey()}
      </div>
    );
  }
}

export default EventTimeline;



// WEBPACK FOOTER //
// ./src/app/components/partials/event-list/event-timeline.jsx